import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah84k3oh653ivfvnnp0';

export default function DiscussionForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('mobile_alt') ?? '').trim();
    if (honeypot) {
      formEl.reset();
      setStatus('success');
      setMessage('留言已提交，谢谢参与讨论。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'mobile_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        formEl.reset();
        setStatus('success');
        setMessage('留言已提交，谢谢参与讨论。');
      } else {
        setStatus('error');
        setMessage(
          isSpam
            ? '这条留言未能通过校验，请调整内容后重试。'
            : `提交失败：${serverMsg || '请稍后重试'}`
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，留言没有提交成功，请稍后重试。');
    }
  };

  return (
    <form
      id="discussion-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
    >
      <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
        <i className="ri-edit-2-line text-primary-500" aria-hidden="true" />
        留下你的观点
      </h3>
      <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
        请围绕事件本身讨论，不要发布他人隐私或未经证实的指控。
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="discussion-nickname" className="mb-1.5 block text-xs font-medium text-foreground-700">
            昵称
          </label>
          <input
            id="discussion-nickname"
            name="nickname"
            type="text"
            required
            maxLength={20}
            placeholder="随便起个名字"
            className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="discussion-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
            邮箱（仅用于回复通知）
          </label>
          <input
            id="discussion-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor="discussion-content" className="mb-1.5 block text-xs font-medium text-foreground-700">
          你的看法（500 字以内）
        </label>
        <textarea
          id="discussion-content"
          name="content"
          required
          maxLength={500}
          rows={4}
          placeholder="说说你的推理过程，或者指出哪条材料有问题…"
          className="w-full resize-y rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
        />
      </div>

      <div className="form-honeypot-guard" aria-hidden="true">
        <label htmlFor="discussion-mobile-alt">手机号（选填）</label>
        <input
          id="discussion-mobile-alt"
          name="mobile_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] leading-relaxed text-foreground-400">
          提交即表示你同意我们公开显示该留言内容。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} aria-hidden="true" />
          {status === 'submitting' ? '提交中…' : '提交留言'}
        </button>
      </div>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-2 text-xs text-secondary-900">
          <i className="ri-checkbox-circle-line" aria-hidden="true" />
          {message}
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-2 text-xs text-primary-800">
          <i className="ri-error-warning-line" aria-hidden="true" />
          {message}
        </p>
      )}
    </form>
  );
}